import type {
  City,
  CityArea,
  DoctorsListOrder,
  ServiceDetails,
} from "@hermes/api";
import type { Temporal } from "@hermes/intl";
import type { GeoPosition } from "@hermes/shared";
import { useMemo } from "react";

import type { DoctorCardService } from "#internal/features/doctor-card";

import { useDoctorsPages } from "./";

export const useCombinedDoctorsPages = ({
  sort,
  targetServices,
  city,
  cityArea,
  clinicId,
  position,
  slotDate,
  enablePromo,
  language,
  insurance,
  currentService,
}: {
  sort?: DoctorsListOrder;
  targetServices?: DoctorCardService[];
  position?: GeoPosition;
  city: City;
  cityArea?: CityArea;
  clinicId?: number;
  slotDate?: Temporal.PlainDate;
  enablePromo?: boolean;
  language?: string;
  insurance?: number;
  currentService?: ServiceDetails;
}) => {
  const shouldApplyFilter = Boolean(insurance || cityArea);

  const filtered = useDoctorsPages({
    sort,
    targetServices,
    city,
    cityArea,
    clinicId,
    position,
    slotDate,
    language,
    insurance,
    currentService,
    nearestCityArea: undefined,
  });

  const filteredDoctorIds = useMemo(() => {
    return filtered.rawPages.flatMap((page) => {
      const doctorList = page.doctors?.results;
      if (!Array.isArray(doctorList)) return [];
      return doctorList.map((doctor) => doctor.id);
    });
  }, [filtered.rawPages]);

  const fallback = useDoctorsPages({
    sort,
    targetServices,
    city,
    clinicId,
    position,
    slotDate,
    enablePromo,
    language,
    currentService,
    nearestCityArea: cityArea ? cityArea.id : undefined,
    ...(insurance && filteredDoctorIds.length > 0
      ? { excludedIds: filteredDoctorIds }
      : {}),
  });

  const fallbackPagesWithoutDuplicates = useMemo(() => {
    return fallback.pages.map((page, index) => {
      const originalRawPage = fallback.rawPages[index];
      const filteredResults = originalRawPage?.doctors?.results.filter(
        (doctor) => !filteredDoctorIds.includes(doctor.id),
      );
      return {
        ...page,
        cards: page.cards?.filter((card) =>
          filteredResults?.some((doc) => doc.id === card.doctor.id),
        ),
      };
    });
  }, [fallback.pages, fallback.rawPages, filteredDoctorIds]);

  return {
    pages: shouldApplyFilter
      ? [...filtered.pages, ...fallbackPagesWithoutDuplicates]
      : fallback.pages,
    filteredCount: shouldApplyFilter ? filtered.pages.length : 0,
    fallbackStartIndex: shouldApplyFilter ? filtered.pages.length : 0,
    fetchPage: (pageIndex: number) => {
      if (shouldApplyFilter) {
        if (pageIndex < filtered.pages.length) {
          filtered.fetchPage(pageIndex);
        } else {
          fallback.fetchPage(pageIndex - filtered.pages.length);
        }
      } else {
        fallback.fetchPage(pageIndex);
      }
    },
    count: shouldApplyFilter
      ? filtered.count !== undefined && fallback.count !== undefined
        ? filtered.count + fallback.count
        : undefined
      : fallback.count,
    limit: filtered.limit,
    premiumCount: filtered.premiumCount,
  };
};
